/**
 * interactions.ts
 * -------------------------------------------------------
 * 职责：订阅 echarts / ZRender 上的鼠标事件，
 *       实现「拖拽不触发点击」、园区散点、政策散点与区块选省/市。
 *       政策模式：全国图单击省区下钻并写入省份筛选；单击省份聚合点仅在右侧展示代表政策，不改左侧筛选、不下钻；省图单击地市更新筛选。
 * -------------------------------------------------------
 */

import type { ECharts } from 'echarts';
import { DRAG_THRESHOLD_PX } from '../mapConstants';
import { provinceAdcodeToGeoName, resolveProvinceAdcodeLoose } from '../provinceAdcode';
import {
    resolveProvinceAdcodeFromChinaMapPixel,
    pixelFromGeo3DClickParams,
} from './geoPickHelper';
import {
    featureLngLatByName,
    resolveProvinceAdcodeFromClickParams,
} from './geoHelpers';
import type {
    Geo3DClickParams,
    MapInteractionDeps,
    PolicyScatterDatum,
    ScatterDatum,
    ZrPointerEvent,
} from '../types';

function applyPolicyScatterSelection(
    deps: MapInteractionDeps,
    datum: {
        representativePolicyId: string;
        value: [number, number, number];
    },
): void {
    const id = String(datum.representativePolicyId);
    if (!id) return;
    deps.setDrillTargetCoord([datum.value[0], datum.value[1]]);
    deps.onSelectRef.current(id);
}

/**
 * 订阅地图交互事件，返回清理函数。
 */
export function subscribeMapInteractions(
    chart: ECharts,
    deps: MapInteractionDeps,
): () => void {
    const zr = chart.getZr();

    let pointerDown = false;
    let movedSinceDown = false;
    let suppressClick = false;
    let downX = 0;
    let downY = 0;

    const onMouseDown = (evt: unknown) => {
        const ev = (evt as ZrPointerEvent).event;
        if (ev && typeof ev.button === 'number' && ev.button !== 0) return;
        pointerDown = true;
        movedSinceDown = false;
        downX = ev?.zrX ?? 0;
        downY = ev?.zrY ?? 0;
    };

    const onMouseMove = (evt: ZrPointerEvent) => {
        if (!pointerDown) return;
        const x = evt.event?.zrX ?? downX;
        const y = evt.event?.zrY ?? downY;
        if (
            !movedSinceDown &&
            (Math.abs(x - downX) > DRAG_THRESHOLD_PX ||
                Math.abs(y - downY) > DRAG_THRESHOLD_PX)
        ) {
            movedSinceDown = true;
        }
    };

    const onMouseUp = () => {
        if (pointerDown && movedSinceDown) {
            suppressClick = true;
        }
        pointerDown = false;
        movedSinceDown = false;
    };

    const onGlobalOut = () => {
        pointerDown = false;
        movedSinceDown = false;
    };

    const onClick = (params: Geo3DClickParams) => {
        if (suppressClick) {
            suppressClick = false;
            return;
        }

        const raw = params.data;

        if (raw && typeof raw === 'object' && 'representativePolicyId' in raw) {
            applyPolicyScatterSelection(deps, raw as PolicyScatterDatum);
            return;
        }

        // scatter3D 点击：优先从 data.parkId 读取，若 echarts-gl 未透传自定义字段则
        // 回退到 dataIndex 在 scatterDataRef 中查找
        if (raw && typeof raw === 'object' && 'parkId' in raw) {
            const datum = raw as ScatterDatum;
            const id = String(datum.parkId);
            if (id) {
                deps.setDrillTargetCoord([datum.value[0], datum.value[1]]);
                deps.onSelectRef.current(id);
            }
            return;
        }

        if (
            params.seriesType === 'scatter3D' &&
            typeof params.dataIndex === 'number' &&
            params.componentType === 'series'
        ) {
            // 政策聚合点在某些渲染场景下不会透传自定义字段，回退到 dataIndex 回查
            if (deps.mapQueryModeRef.current === 'policies') {
                const policyItems = deps.policyScatterDataRef.current;
                const policyDatum = policyItems[params.dataIndex];
                if (policyDatum?.representativePolicyId) {
                    applyPolicyScatterSelection(deps, policyDatum);
                    return;
                }
            }

            const items = deps.scatterDataRef.current;
            const datum = items[params.dataIndex];
            if (datum?.parkId) {
                deps.setDrillTargetCoord([datum.value[0], datum.value[1]]);
                deps.onSelectRef.current(datum.parkId);
            }
            return;
        }

        const mapName = deps.viewCtxRef.current.mapName;

        // —— 省下钻图：政策模式下点击地级区块 ——
        if (mapName !== 'china') {
            if (deps.mapQueryModeRef.current !== 'policies') return;
            const cityName =
                typeof params.name === 'string' ? params.name.trim() : '';
            if (!cityName) return;
            deps.onPolicySelectCityNameRef.current?.(cityName);
            return;
        }

        // —— 全国图 ——
        let ad = resolveProvinceAdcodeFromClickParams(params);
        if (!ad) {
            const pix = pixelFromGeo3DClickParams(params);
            if (pix) {
                ad = resolveProvinceAdcodeFromChinaMapPixel(chart, pix.x, pix.y);
            }
        }
        if (!ad) return;

        if (deps.mapQueryModeRef.current === 'policies') {
            const currentProvince = deps.policyProvinceRef.current?.trim();
            const currentProvinceAd = currentProvince
                ? resolveProvinceAdcodeLoose(currentProvince)
                : null;

            // 省级筛选且已选定省份时，优先按省份聚合点语义选中代表政策，避免落入区块下钻导致“点不动”。
            if (
                deps.policyLevelRef.current === 'provincial' &&
                currentProvinceAd &&
                currentProvinceAd === ad
            ) {
                const sameProvinceAgg = deps.policyScatterDataRef.current.find((item) => {
                    if (item.provinceName) {
                        return resolveProvinceAdcodeLoose(item.provinceName) === ad;
                    }
                    const labelProvince = item.name
                        .split('·')[0]
                        .replace(/（省级）\s*$/, '')
                        .trim();
                    return resolveProvinceAdcodeLoose(labelProvince) === ad;
                });
                if (sameProvinceAgg?.representativePolicyId) {
                    applyPolicyScatterSelection(deps, sameProvinceAgg);
                }
                return;
            }

            const geoName = provinceAdcodeToGeoName(ad);
            if (!geoName) return;
            deps.policyDrillByUserRef.current = true;
            deps.setDrillTargetCoord(null);
            deps.onDrillProvinceRef.current?.();
            deps.setFocusAdcode(ad);
            deps.setLayoutMode('provinceDrill');
            deps.onPolicyDrillProvinceNameRef.current?.(geoName);
            return;
        }

        deps.setDrillTargetCoord(null);
        deps.onDrillProvinceRef.current?.();
        deps.setFocusAdcode(ad);
        deps.setLayoutMode('provinceDrill');
    };

    const onDblClick = (params: Geo3DClickParams) => {
        const raw = params.data;
        if (raw && typeof raw === 'object' && ('parkId' in raw || 'representativePolicyId' in raw)) {
            return;
        }

        if (deps.mapQueryModeRef.current !== 'policies') return;

        const mapName = deps.viewCtxRef.current.mapName;

        if (mapName === 'china') {
            return;
        }

        const cityName =
            typeof params.name === 'string' ? params.name.trim() : '';
        const geo = deps.provinceGeoForPickRef.current;
        if (cityName && geo) {
            const ll = featureLngLatByName(geo, cityName);
            if (ll) deps.setDrillTargetCoord(ll);
        }
    };

    zr.on('mousedown', onMouseDown);
    zr.on('mousemove', onMouseMove);
    zr.on('mouseup', onMouseUp);
    zr.on('globalout', onGlobalOut);
    chart.on('click', onClick);
    chart.on('dblclick', onDblClick);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
        window.removeEventListener('mouseup', onMouseUp);
        zr.off('mousedown', onMouseDown);
        zr.off('mousemove', onMouseMove);
        zr.off('mouseup', onMouseUp);
        zr.off('globalout', onGlobalOut);
        chart.off('click', onClick);
        chart.off('dblclick', onDblClick);
    };
}
